<template>
  <div class="nfb">
    <!-- แถวหลัก: ปุ่มหมวดหมู่ทั้งหมด (ตำแหน่งคงที่ ไม่ขยับเมื่อเปลี่ยนตัวเลือก) -->
    <div class="nfb-row nfb-row-main">
      <div class="nfb-block">
        <label>ช่วงเวลา</label>
        <div class="nfb-seg">
          <button
            v-for="m in periodModes"
            :key="m.value"
            type="button"
            :class="{ on: periodMode === m.value }"
            @click="setPeriodMode(m.value)"
          >
            {{ m.text }}
          </button>
        </div>
      </div>

      <div class="nfb-block">
        <label>พื้นที่</label>
        <div class="nfb-seg">
          <button
            v-for="t in areaTypes"
            :key="t.value"
            type="button"
            :class="{ on: local.areaType === t.value }"
            @click="setAreaType(t.value)"
          >
            {{ t.text }}
          </button>
        </div>
      </div>

      <div class="nfb-block">
        <label>สิทธิ</label>
        <b-form-select
          v-model="local.subitem"
          :options="subitemOptions"
          size="sm"
          class="nfb-select"
          @change="emitChange"
        />
      </div>

      <button
        type="button"
        class="nfb-export ml-auto"
        :disabled="loading"
        @click="$emit('export')"
      >
        ⬇ ดาวน์โหลด Excel
      </button>
    </div>

    <!-- แถวรอง: ตัวเลือกย่อยที่ขยายได้ (เดือน/ปี/พื้นที่) — อยู่แยกแถวจึงไม่ดันแถวหลัก -->
    <div v-if="periodMode !== 'all' || local.areaType !== 'all'" class="nfb-row nfb-row-detail">
      <!-- รายปี → ปีปฏิทิน + ปีงบประมาณ -->
      <template v-if="periodMode === 'year'">
        <div class="nfb-subgroup">
          <span class="nfb-sublabel">ปี</span>
          <div class="nfb-seg nfb-seg-sm">
            <button
              v-for="y in calendarYearOptions"
              :key="y.value"
              type="button"
              :class="{ on: local.period === y.value }"
              @click="setPeriod(y.value)"
            >
              {{ y.text }}
            </button>
          </div>
        </div>
        <div class="nfb-subgroup">
          <span class="nfb-sublabel">ปีงบ</span>
          <div class="nfb-seg nfb-seg-sm">
            <button
              v-for="y in fiscalYearOptions"
              :key="y.value"
              type="button"
              :class="{ on: local.period === y.value }"
              @click="setPeriod(y.value)"
            >
              {{ y.text }}
            </button>
          </div>
        </div>
      </template>

      <!-- รายเดือน → เลือกเดือน (พิมพ์ค้นหาได้) -->
      <div v-if="periodMode === 'month'" class="nfb-subgroup">
        <span class="nfb-sublabel">เดือน</span>
        <multiselect
          class="nfb-ms nfb-ms-month"
          :value="selectedMonth"
          :options="monthOptions"
          :multiple="false"
          :allow-empty="false"
          :searchable="true"
          placeholder="เลือกเดือน"
          label="text"
          track-by="value"
          selectLabel=""
          selectedLabel=""
          deselectLabel=""
          @input="onMonthInput"
        >
          <span slot="noResult">ไม่พบเดือน</span>
        </multiselect>
      </div>

      <!-- พื้นที่: เลือกได้หลายค่า (พิมพ์ค้นหาได้) -->
      <div v-if="local.areaType !== 'all'" class="nfb-subgroup nfb-subgroup-area">
        <span class="nfb-sublabel">{{ areaTypeLabel }}</span>
        <multiselect
          class="nfb-ms nfb-ms-area"
          :value="selectedAreas"
          :options="areaOptions"
          :multiple="true"
          :close-on-select="false"
          :searchable="true"
          :placeholder="areaPlaceholder"
          label="text"
          track-by="value"
          selectLabel=""
          selectedLabel=""
          deselectLabel=""
          @input="onAreaInput"
        >
          <span slot="noResult">ไม่พบพื้นที่</span>
        </multiselect>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Multiselect from "vue-multiselect";
import { PROVINCES } from "@/constants/thaiProvinces";

const PROGRAM_START = moment("2023-10-01"); // เริ่มไทม์ไลน์ = ปีงบ 2567 (ต.ค. 2566)
const TH_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
// label เดือนแบบไทย + พ.ศ. (ให้ตรงกับแท็บรายปี 2566/67/68/69)
const thaiMonthLabel = (m) => `${TH_MONTHS[m.month()]} ${m.year() + 543}`;

// Zone value = zone number string ("1".."13"); region value = region name;
// province value = province name — matching the frozen `area` query contract.
const ZONE_OPTIONS = Array.from({ length: 13 }, (_, i) => ({
  value: String(i + 1),
  text: `เขต ${i + 1}`,
}));
const REGION_OPTIONS = [
  { value: "เหนือ", text: "ภาคเหนือ" },
  { value: "กลาง", text: "ภาคกลาง" },
  { value: "ตะวันออกเฉียงเหนือ", text: "ภาคอีสาน" },
  { value: "ตะวันออก", text: "ภาคตะวันออก" },
  { value: "ตะวันตก", text: "ภาคตะวันตก" },
  { value: "ใต้", text: "ภาคใต้" },
];

export default {
  name: "NhsoFilterBar",
  components: { Multiselect },
  props: {
    value: { type: Object, required: true }, // { period, areaType, area, subitem } — area = comma list
    loading: { type: Boolean, default: false },
  },
  data() {
    return { local: { ...this.value } };
  },
  computed: {
    periodModes() {
      return [
        { value: "all", text: "ตั้งแต่เริ่มโครงการ" },
        { value: "year", text: "รายปี" },
        { value: "month", text: "รายเดือน" },
      ];
    },
    periodMode() {
      const p = this.local.period;
      if (/^(CY|FY)\d{4}$/.test(p)) return "year";
      if (/^\d{4}-\d{2}$/.test(p)) return "month";
      return "all"; // ALL (และ preset เก่า MTD/YTD/L12M) แสดงเป็น "ตั้งแต่เริ่มโครงการ"
    },
    calendarYearOptions() {
      const startY = PROGRAM_START.year();
      const cur = moment().year();
      const out = [];
      for (let y = cur; y >= startY; y--) out.push({ value: `CY${y}`, text: String(y + 543) });
      return out;
    },
    fiscalYearOptions() {
      const startFy = PROGRAM_START.month() >= 9 ? PROGRAM_START.year() + 1 : PROGRAM_START.year();
      const now = moment();
      const curFy = now.month() >= 9 ? now.year() + 1 : now.year();
      const out = [];
      for (let fy = curFy; fy >= startFy; fy--) {
        out.push({ value: `FY${fy}`, text: String((fy + 543) % 100).padStart(2, "0") });
      }
      return out;
    },
    monthOptions() {
      const out = [];
      let m = PROGRAM_START.clone();
      const now = moment();
      while (m.isSameOrBefore(now, "month")) {
        out.push({ value: m.format("YYYY-MM"), text: thaiMonthLabel(m) });
        m = m.clone().add(1, "month");
      }
      return out.reverse();
    },
    selectedMonth() {
      return this.monthOptions.find(o => o.value === this.local.period) || null;
    },
    areaTypes() {
      return [
        { value: "all", text: "ทั้งประเทศ" },
        { value: "zone", text: "เขตสุขภาพ" },
        { value: "region", text: "ภาค" },
        { value: "province", text: "จังหวัด" },
      ];
    },
    areaOptions() {
      if (this.local.areaType === "zone") return ZONE_OPTIONS;
      if (this.local.areaType === "region") return REGION_OPTIONS;
      return PROVINCES.map(p => ({ value: p, text: p }));
    },
    areaTypeLabel() {
      if (this.local.areaType === "zone") return "เขตสุขภาพ";
      if (this.local.areaType === "region") return "ภาค";
      return "จังหวัด";
    },
    areaPlaceholder() {
      if (this.local.areaType === "zone") return "เลือกเขตสุขภาพ (เลือกได้หลายเขต)";
      if (this.local.areaType === "region") return "เลือกภาค (เลือกได้หลายภาค)";
      return "พิมพ์ค้นหาจังหวัด (เลือกได้หลายจังหวัด)";
    },
    selectedAreas() {
      const tokens = String(this.local.area || "")
        .split(",")
        .filter(Boolean);
      return this.areaOptions.filter(o => tokens.includes(o.value));
    },
    subitemOptions() {
      return [
        { value: "ALL", text: "ทุกสิทธิ (D10 + D11 + D20)" },
        { value: "D10", text: "D10 บัตร ปชช. ใบเดียว" },
        { value: "D11", text: "D11" },
        { value: "D20", text: "D20" },
      ];
    },
  },
  watch: {
    // Keep the local copy in sync if the parent replaces the filter object.
    value: {
      handler(v) {
        this.local = { ...v };
      },
      deep: true,
    },
  },
  methods: {
    setPeriod(period) {
      this.local.period = period;
      this.emitChange();
    },
    setPeriodMode(mode) {
      if (mode === "all") this.local.period = "ALL";
      else if (mode === "year") this.local.period = this.calendarYearOptions[0].value;
      else if (mode === "month") this.local.period = this.monthOptions[0].value;
      this.emitChange();
    },
    onMonthInput(opt) {
      if (opt) this.setPeriod(opt.value);
    },
    setAreaType(t) {
      this.local.areaType = t;
      // ให้มีค่าที่ valid เสมอ: ทั้งประเทศ → ALL, อื่น ๆ → เลือกตัวแรก
      this.local.area = t === "all" ? "ALL" : this.areaOptions[0].value;
      this.emitChange();
    },
    onAreaInput(arr) {
      // ล้างจนว่าง → กลับไปทั้งประเทศ (กัน scope ว่างที่ทำให้ค่าทุกอย่างเป็น 0)
      if (!arr || !arr.length) {
        this.setAreaType("all");
        return;
      }
      this.local.area = arr.map(o => o.value).join(",");
      this.emitChange();
    },
    emitChange() {
      this.$emit("change", { ...this.local });
    },
  },
};
</script>

<style scoped>
.nfb {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 14px;
  background: var(--nd-card);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  margin-bottom: 18px;
  box-shadow: var(--nd-shadow);
}
.nfb-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
}
.nfb-row-detail {
  padding-top: 12px;
  border-top: 1px dashed var(--nd-border);
}
.nfb-block {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.nfb label {
  font-size: 12px;
  font-weight: 600;
  color: var(--nd-muted);
  margin: 0;
}
.nfb-select {
  width: auto;
}
.nfb-subgroup {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.nfb-subgroup-area {
  flex: 1 1 260px;
  align-items: flex-start;
}
.nfb-subgroup-area .nfb-sublabel {
  padding-top: 8px;
}
.nfb-sublabel {
  font-size: 11px;
  font-weight: 600;
  color: var(--nd-muted);
}
.nfb-seg {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--nd-border);
  border-radius: 9px;
  background: var(--nd-surface2);
}
.nfb-seg button {
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  border: 0;
  background: transparent;
  color: var(--nd-text2);
  padding: 6px 13px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.nfb-seg-sm button {
  padding: 5px 10px;
  font-size: 12px;
}
.nfb-seg button:hover {
  color: var(--nd-text);
}
.nfb-seg button.on {
  background: var(--nd-card);
  color: var(--nd-accent);
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
.nfb-ms {
  font-size: 13px;
}
.nfb-ms-month {
  min-width: 160px;
  max-width: 200px;
}
.nfb-ms-area {
  flex: 1;
  min-width: 220px;
}
.nfb-export {
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: var(--nd-accent);
  border: 0;
  border-radius: 9px;
  padding: 8px 15px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(22, 104, 179, 0.3);
  transition: filter 0.15s;
}
.nfb-export:hover:not(:disabled) {
  filter: brightness(1.06);
}
.nfb-export:disabled {
  opacity: 0.6;
  cursor: default;
}
.ml-auto {
  margin-left: auto;
}
</style>

<style>
/* vue-multiselect: ปรับให้เข้ากับธีม dashboard (ไม่ scoped เพราะ lib ใช้ global class) */
.nfb-ms.multiselect {
  min-height: 34px;
}
.nfb-ms .multiselect__tags {
  min-height: 34px;
  padding: 4px 32px 0 8px;
  border-color: var(--nd-border);
  border-radius: 8px;
  font-size: 13px;
}
.nfb-ms .multiselect__select {
  height: 32px;
}
.nfb-ms .multiselect__tag {
  background: var(--nd-accent);
  color: #fff;
  margin-bottom: 2px;
}
.nfb-ms .multiselect__tag-icon::after {
  color: #ffffff;
}
.nfb-ms .multiselect__tag-icon:hover {
  background: rgba(255, 255, 255, 0.25);
}
.nfb-ms .multiselect__single {
  font-size: 13px;
  margin-bottom: 4px;
  padding-top: 2px;
}
.nfb-ms .multiselect__option--highlight,
.nfb-ms .multiselect__option--highlight::after {
  background: var(--nd-accent);
}
.nfb-ms .multiselect__placeholder {
  font-size: 13px;
  color: var(--nd-muted);
  padding-top: 2px;
  margin-bottom: 4px;
}
</style>
